/*
 * The desktop app is a Tauri shell around this web app: the window loads app.tank.chat, so every
 * screen here is the desktop app's too. What differs is what a browser tab cannot do — native
 * notifications, a dock badge, `tank://` links, opening external URLs in the browser, self-update —
 * and each of those is a small call over Tauri's IPC, made only when the shell is present.
 *
 * Everything Tauri-specific is imported lazily so the browser bundle never evaluates it.
 */

export const DOWNLOADS_BASE = "https://downloads.tank.chat/desktop/latest";

export type DesktopPlatform = "macos" | "windows" | "linux";

export interface DesktopDownload {
  platform: DesktopPlatform;
  label: string;
  url: string;
  /** Second option on the same platform (Linux .deb next to the AppImage). */
  alt?: { label: string; url: string };
}

export const DOWNLOADS: DesktopDownload[] = [
  { platform: "macos", label: "Download for macOS", url: `${DOWNLOADS_BASE}/TANK-macOS.dmg` },
  { platform: "windows", label: "Download for Windows", url: `${DOWNLOADS_BASE}/TANK-Windows-x64-setup.exe` },
  {
    platform: "linux",
    label: "Download for Linux (AppImage)",
    url: `${DOWNLOADS_BASE}/TANK-Linux-x86_64.AppImage`,
    alt: { label: ".deb", url: `${DOWNLOADS_BASE}/TANK-Linux-amd64.deb` },
  },
];

/** True inside the desktop app (Tauri injects this before any script runs). */
export function isDesktop(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Which installer a browser's user agent should be offered first; undefined on phones and unknowns. */
export function platformFromUserAgent(ua: string): DesktopPlatform | undefined {
  if (/iPhone|iPad|Android/i.test(ua)) return undefined;
  if (/Mac OS X|Macintosh/.test(ua)) return "macos";
  if (/Windows/.test(ua)) return "windows";
  if (/Linux|X11/.test(ua)) return "linux";
  return undefined;
}

/**
 * A `tank://` link as an in-app path. The scheme is shared with mobile: `tank://w/acme/c/123`
 * opens the same place on both. Anything that is not a plain path (another host, `..`) is refused.
 */
export function deepLinkToPath(url: string): string | undefined {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return undefined;
  }
  if (u.protocol !== "tank:") return undefined;
  // `tank://w/acme` parses the first segment as the host; put it back.
  const path = `/${u.host}${u.pathname}`.replace(/\/+/g, "/");
  if (path.includes("..") || !/^\/[A-Za-z0-9/_\-.%]*$/.test(path)) return undefined;
  return `${path}${u.search}`;
}

/** Native notification; a no-op outside the desktop app. Asks permission the first time. */
export async function notifyDesktop(title: string, body: string): Promise<void> {
  if (!isDesktop()) return;
  const n = await import("@tauri-apps/plugin-notification");
  let ok = await n.isPermissionGranted();
  if (!ok) ok = (await n.requestPermission()) === "granted";
  if (ok) n.sendNotification({ title, body });
}

/** Dock / taskbar badge; a no-op outside the desktop app. */
export async function setDesktopBadge(count: number): Promise<void> {
  if (!isDesktop()) return;
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  await getCurrentWindow().setBadgeCount(count > 0 ? count : undefined);
}

/** Whether the desktop window has focus; `document.hasFocus()` elsewhere. */
export async function desktopFocused(): Promise<boolean> {
  if (!isDesktop()) return document.hasFocus();
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  return getCurrentWindow().isFocused();
}

/** Open a URL in the system browser; a no-op outside the desktop app (the browser handles it). */
export async function openExternal(url: string): Promise<void> {
  if (!isDesktop()) return;
  const { openUrl } = await import("@tauri-apps/plugin-opener");
  await openUrl(url);
}

/** Subscribe to `tank://` links delivered to the running app; returns the unsubscribe. */
export async function onDeepLink(cb: (path: string) => void): Promise<() => void> {
  if (!isDesktop()) return () => undefined;
  const dl = await import("@tauri-apps/plugin-deep-link");
  const handle = (urls: string[] | null) => {
    for (const u of urls ?? []) {
      const path = deepLinkToPath(u);
      if (path) cb(path);
    }
  };
  handle(await dl.getCurrent());
  return dl.onOpenUrl(handle);
}

export interface DesktopUpdate {
  version: string;
  /** Download, install and relaunch. */
  install: () => Promise<void>;
}

/** The update waiting for this app, if any; undefined outside the desktop app or when current. */
export async function checkDesktopUpdate(): Promise<DesktopUpdate | undefined> {
  if (!isDesktop()) return undefined;
  const { check } = await import("@tauri-apps/plugin-updater");
  const update = await check();
  if (!update) return undefined;
  return {
    version: update.version,
    install: async () => {
      await update.downloadAndInstall();
      const { relaunch } = await import("@tauri-apps/plugin-process");
      await relaunch();
    },
  };
}
